import { Ambulance, Clock, MapPin, HeartPulse, Wrench, FileText } from 'lucide-react';
import DigitalSignatureBlock from '../components/documents/DigitalSignatureBlock';

interface Props {
  data: {
    dispatchId: string;
    callType: string;
    priority: string;
    pickupLocation: string;
    dropLocation: string;
    dispatchTime: string;
    arrivalTime: string;
    totalTime: string;
    distance: string;
    crewParamedic: string;
    ambulanceNo: string;
    patientCondition: {
      time: string;
      bp: string;
      pulse: string;
      spo2: string;
      temp: string;
      gcs: string;
      notes: string;
    }[];
    equipment: {
      item: string;
      status: string;
      medication?: string;
      dose?: string;
      time?: string;
      givenBy?: string;
    }[];
    handoverNotes?: string;
    signatories: {
      name: string;
      role: string;
      nhcId?: string;
      licenseNo?: string;
      institution?: string;
    }[];
  };
}

function getPriorityStyle(priority: string | undefined) {
  const p = (priority || '').toUpperCase();
  if (p === 'CRITICAL' || p === 'EMERGENCY') return 'bg-red-100 text-red-700 border-red-300';
  if (p === 'HIGH' || p === 'URGENT') return 'bg-orange-100 text-orange-700 border-orange-300';
  return 'bg-green-100 text-green-700 border-green-300';
}

export default function AmbulanceCertificateTemplate({ data }: Props) {
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Ambulance className="w-4 h-4 text-gray-700" />
          <span className="text-xs font-bold uppercase tracking-wider text-gray-700">
            Ambulance Service Report
          </span>
        </div>
        <span
          className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded border ${getPriorityStyle(data.priority)}`}
        >
          {data.priority}
        </span>
      </div>

      {/* Dispatch Details Grid */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 bg-gray-50 px-3 py-2.5 border border-gray-200 rounded text-[10px]">
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Dispatch ID:</span>
          <span className="text-gray-800 font-mono">{data.dispatchId}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Call Type:</span>
          <span className="text-gray-800">{data.callType}</span>
        </div>
        <div className="flex items-start gap-2">
          <MapPin className="w-3 h-3 text-gray-500 mt-0.5 shrink-0" />
          <span className="font-semibold text-gray-600">Pickup:</span>
          <span className="text-gray-800">{data.pickupLocation}</span>
        </div>
        <div className="flex items-start gap-2">
          <MapPin className="w-3 h-3 text-gray-500 mt-0.5 shrink-0" />
          <span className="font-semibold text-gray-600">Drop:</span>
          <span className="text-gray-800">{data.dropLocation}</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-3 h-3 text-gray-500" />
          <span className="font-semibold text-gray-600">Dispatch:</span>
          <span className="text-gray-800">{data.dispatchTime}</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-3 h-3 text-gray-500" />
          <span className="font-semibold text-gray-600">Arrival:</span>
          <span className="text-gray-800">{data.arrivalTime}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Total Time:</span>
          <span className="text-gray-800">{data.totalTime}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Distance:</span>
          <span className="text-gray-800">{data.distance}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Crew / Paramedic:</span>
          <span className="text-gray-800">{data.crewParamedic}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Ambulance No:</span>
          <span className="text-gray-800 font-mono">{data.ambulanceNo}</span>
        </div>
      </div>

      {/* Patient Condition Monitoring */}
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <HeartPulse className="w-3 h-3 text-gray-600" />
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
            Patient Condition Monitoring
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[10px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Time
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  BP
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Pulse
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  SpO2
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Temp
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  GCS
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody>
              {data.patientCondition.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-700 font-mono">
                    {row.time}
                  </td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-800">{row.bp}</td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-800">{row.pulse}</td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-800">{row.spo2}</td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-800">{row.temp}</td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-800">{row.gcs}</td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-600">{row.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Equipment & Medication */}
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <Wrench className="w-3 h-3 text-gray-600" />
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
            Equipment &amp; Medication Administered
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[10px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Item
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Status
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Medication
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Dose
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Time
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Given By
                </th>
              </tr>
            </thead>
            <tbody>
              {data.equipment.map((eq, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="py-1 px-1.5 border border-gray-200 font-medium text-gray-800">
                    {eq.item}
                  </td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-700">{eq.status}</td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-700">
                    {eq.medication || '—'}
                  </td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-700">
                    {eq.dose || '—'}
                  </td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-700 font-mono">
                    {eq.time || '—'}
                  </td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-700">
                    {eq.givenBy || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Handover Notes */}
      {data.handoverNotes && (
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <FileText className="w-3 h-3 text-gray-600" />
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
              Handover Notes
            </h3>
          </div>
          <p className="text-[10px] text-gray-700 leading-relaxed pl-1 bg-gray-50 px-3 py-2 border border-gray-200 rounded">
            {data.handoverNotes}
          </p>
        </div>
      )}

      {/* Digital Signatures */}
      <DigitalSignatureBlock signatories={data.signatories} />
    </div>
  );
}
